import { ChangeDetectionStrategy, Component, Injector, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { FileuploadComponent } from '@shared/components/fileupload/fileupload.component';
import { SubMenuItem } from '@shared/components/header-sub-menu/header-sub-menu.component';
import { MesWorkTaskTransferService } from '@shared/services/export-import-services/mesWorkTaskTransfer.service';
import { ImportValidatorService } from '@shared/services/filevalidators/Importvalidator.service';
import { NotificationService } from '@shared/services/notification.service';
import { IWorktask, WorktaskService } from '@shared/services/worktask.service';
import { map, Subject, takeUntil } from 'rxjs';
import { WorktaskDialogComponent } from './worktask-dialog';

interface ITableViewModelItem {
	isActive: boolean;
	alias: string;
	category: string;
	terminal: string;
	id: string;
	name: string;
	salary: string;
	task: IWorktask;
}

@Component({
	selector: 'app-workworktasks-list',
	templateUrl: './worktasks-list.component.html',
	styleUrls: ['./worktasks-list.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class WorktasksListComponent {
	offstampId = '00000000-0000-0000-0000-000000000000';
	displayedColumns = ['name', 'category', 'salary', 'terminal', 'isActive', 'menu'];

	dataSource = new MatTableDataSource<ITableViewModelItem>();
	showSearch = false;
	isLoading = true;
	showInactive = false;
	categoryNames: string[];

	private destroy$: Subject<void> = new Subject<void>();

	favoriteItems: SubMenuItem[] = [
		{
			icon: () => 'acies_add',
			text: () => 'mes.administration.worktasks.newbtn',
			click: () => this.openWorktask(null),
		},
	];

	menuItems: SubMenuItem[] = [
		{
			icon: () => 'acies_download',
			text: () => 'mes.administration.worktasks.import.button',
			click: () => this.onImport(),
		},
		{
			icon: () => 'acies_upload',
			text: () => 'mes.administration.worktasks.export.button',
			click: () => this.onExport(),
		},
	];

	constructor(
		public dialog: MatDialog,
		public worktaskService: WorktaskService,
		public workTaskTransferService: MesWorkTaskTransferService,
		public notificationService: NotificationService,
		public injector: Injector,
	) {}

	@ViewChild(MatPaginator) paginator: MatPaginator;
	@ViewChild(MatSort) sort: MatSort;

	ngAfterViewInit() {
		this.dataSource.paginator = this.paginator;
		this.dataSource.sort = this.sort;
		this.updateList();
	}

	private getITableViewModelItem(task: IWorktask): ITableViewModelItem {
		const category = task.tags['categoryName'] ?? '';
		const salary = task.tags['salarytype'] ?? '';
		const terminal = task.tags['terminal'] ?? '';
		return {
			isActive: task.isActive,
			alias: task.alias,
			category,
			terminal,
			id: task.id,
			name: task.name,
			salary,
			task,
		};
	}

	private getTableViewModel(tasks: IWorktask[]): ITableViewModelItem[] {
		return tasks.map((task) => this.getITableViewModelItem(task));
	}

	updateList() {
		this.isLoading = true;
		this.worktaskService
			.getWorktasks()
			.pipe(map((worktasks) => (this.showInactive ? worktasks.result : worktasks.result.filter((worktask) => worktask.isActive))))
			.subscribe((data) => {
				this.dataSource.data = this.getTableViewModel(data);
				this.dataSource.sort = this.sort;
				this.onSort(this.sort);
				// Extract unique category names for use in the new/edit dialog.
				this.categoryNames = Array.from(new Set(data.filter((c) => (c.tags['categoryName'] ?? '') !== '').map((c) => c.tags['categoryName']))).sort();
				this.categoryNames.unshift('');
				this.isLoading = false;
			});
	}

	applyFilter(event: KeyboardEvent) {
		if (event.target instanceof HTMLInputElement) {
			const filterValue = event.target.value;
			this.dataSource.filter = filterValue.trim().toLowerCase();
		}
	}

	openWorktask(worktask: IWorktask | null) {
		const dialogRef = this.dialog.open(WorktaskDialogComponent, {
			disableClose: true,
			width: '450px',
			data: { worktask, categoryNames: this.categoryNames },
		});
		dialogRef.componentInstance.saved.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.updateList();
		});
	}

	toggleShowInactive() {
		this.showInactive = !this.showInactive;
		this.updateList();
	}

	onToggleActive(workTask: IWorktask) {
		workTask.isActive = !workTask.isActive;
		this.worktaskService.update(workTask).subscribe(() => {
			this.updateList();
		});
	}

	public onSort(sort: Sort) {
		if (sort.direction === '') {
			this.sort.sort({ id: 'alias', start: 'asc', disableClear: true });
		}
	}

	onExport() {
		const worktasks = this.dataSource.data.map((item) => item.task).filter((wt) => wt.id !== this.offstampId);

		this.workTaskTransferService.exportData(worktasks).subscribe({
			next: () => {
				this.notificationService.notify(['mes.administration.worktasks.export.notification.success.header'], ['mes.administration.worktasks.export.notification.success.content'], 'success');
			},
			error: (error) => {
				console.error('Failed to export data', error);
				this.notificationService.notify(['mes.administration.worktasks.export.notification.error.header'], ['mes.administration.worktasks.export.notification.error.content'], 'error');
			},
		});
	}

	onImport() {
		const dialogRef = this.dialog.open(FileuploadComponent, {
			width: '450px',
			data: {
				headingKey: 'mes.administration.worktasks.import.title',
				subHeadingKey: 'mes.administration.worktasks.import.subtitle',
				confirmButtonKey: 'mes.administration.worktasks.import.button',
				notificationHeader: 'mes.administration.worktasks.import.notification.success.header',
				notificationContent: 'mes.administration.worktasks.import.notification.success.content',
				notificationErrorHeader: 'mes.administration.worktasks.import.notification.error.header',
				notificationErrorContent: 'mes.administration.worktasks.import.notification.error.content',
				notificationClientErrorHeader: 'mes.administration.worktasks.import.notification.clientError.header',
				notificationClientErrorContent: 'mes.administration.worktasks.import.notification.clientError.content',
				detailedClientErrorMessage: 'fileuploader.detailed-client-error-message',
				allowedFileTypes: ['.xlsx'],
				validatorService: this.injector.get(ImportValidatorService),
				data: this.workTaskTransferService,
				optionalFields: [
					{
						name: 'overwrite',
						titleKey: 'mes.administration.worktasks.import.overwrite',
						type: 'checkbox',
						value: false,
					},
				],
			},
		});

		dialogRef.afterClosed().subscribe(() => {
			this.updateList();
		});
	}
}
